import React, { useState } from 'react';
import { Icons } from '../lib/Icons';
import { 
  getAvailableDownloads, 
  getDownloadTypeName, 
  getDownloadTypeIcon 
} from '../api/downloadableResources';
import GenerateResourceModal from './GenerateResourceModal';

/**
 * Panel component for displaying and generating downloadable resources
 * Uses consistent brand styling with SVG icons - no emojis
 */
const DownloadableResourcesPanel = ({ 
  activity, 
  interventionData = null,
  studentNames = [],
  targets = [],
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDownloadType, setSelectedDownloadType] = useState(null);
  const [generatedResources, setGeneratedResources] = useState([]);

  const availableDownloads = getAvailableDownloads(activity);

  const handleGenerateClick = (downloadType) => {
    setSelectedDownloadType(downloadType);
    setIsModalOpen(true);
  };

  const handleResourceGenerated = (resource) => {
    setGeneratedResources(prev => [...prev, resource]);
    setIsModalOpen(false);
  };

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <Icons.Document className="w-5 h-5" />;
  };

  if (availableDownloads.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icons.Download className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-gray-900">Downloadable Resources</h3>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Generate AI-powered resources to support this activity. All resources use consistent SEN Curriculum branding.
      </p>

      {/* Available download types */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {availableDownloads.map((downloadType) => (
          <button
            key={downloadType}
            onClick={() => handleGenerateClick(downloadType)}
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left group"
          >
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600 group-hover:bg-indigo-200">
              {getIcon(getDownloadTypeIcon(downloadType))}
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {getDownloadTypeName(downloadType)}
              </div>
              <div className="text-xs text-gray-500">
                {downloadType === 'worksheet' && 'Session exercises & activities'}
                {downloadType === 'progress_sheet' && 'Weekly target tracking'}
                {downloadType === 'story' && 'Sensory story with props'}
                {downloadType === 'visual_support' && 'Comic strip or visual schedule'}
              </div>
            </div>
            <Icons.ArrowRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-indigo-600" />
          </button>
        ))}
      </div>

      {/* Recently generated resources */}
      {generatedResources.length > 0 && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Icons.Clock className="w-4 h-4" />
            Recently Generated
          </h4>
          <ul className="space-y-2">
            {generatedResources.map((resource, index) => (
              <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icons.DocumentCheck className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-700">{resource.filename}</span>
                  <span className="text-xs text-gray-500">
                    ({Math.round(resource.fileSize / 1024)} KB)
                  </span>
                </div>
                <a
                  href={resource.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  <Icons.Download className="w-4 h-4" />
                  Download
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Generate Resource Modal */}
      <GenerateResourceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        downloadType={selectedDownloadType}
        activity={activity}
        interventionData={interventionData}
        studentNames={studentNames}
        targets={targets}
        onGenerated={handleResourceGenerated}
      />
    </div>
  );
};

export default DownloadableResourcesPanel;



